/* Find Your Watch — quiz logic (v4, metafield-driven)
 *
 * Source of truth: assets/quiz_manifest.json, generated by
 * build_quiz_manifest.py from the live custom.* storefront-filter metafields
 * (brand / model / case_size / material / …) and current inventory. The
 * manifest defines:
 *   - which brand tiles are live,
 *   - a DYNAMIC follow-up question per brand whose options are derived from the
 *     metafield values actually present across that brand's in-stock pieces
 *     (occasion-from-model first, falling back to material / case_size; brands
 *     that don't split skip the follow-up),
 *   - per-product {facet value, shape, price band} so the Shape and Budget
 *     steps only ever offer combinations we really carry,
 *   - a flat Hidden Gems bucket (no follow-up) of everything not on a tile.
 *
 * Every option a shopper can tap is non-empty by construction. At results time
 * we fetch live title/price/image/availability for the chosen bucket's product
 * ids via the Storefront API, so sold pieces drop out and prices stay current.
 */

(function () {
  'use strict';

  const root = document.querySelector('.dvw-quiz');
  if (!root) return;

  const RESULTS_PER_PAGE = parseInt(root.dataset.resultsPerPage || '24', 10);
  const ARCHIVE_MAX = parseInt(root.dataset.archiveMax || '20', 10);
  const MANIFEST_URL = root.dataset.manifestUrl || '';

  const ANALYTICS_ENDPOINT =
    root.dataset.analyticsEndpoint ||
    'https://dvw-quiz-analytics.dannysvintagewatches.workers.dev/collect';

  const SF_TOKEN = root.dataset.storefrontToken || '';
  const SF_VERSION = root.dataset.storefrontApiVersion || '2025-01';
  const SF_DOMAIN = root.dataset.storefrontDomain || location.host;
  const SF_URL = `https://${SF_DOMAIN}/api/${SF_VERSION}/graphql.json`;

  const SESSION_ID =
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    String(Date.now()) + Math.random().toString(36).slice(2);

  // Persistent device ID — generated once per browser and reused across visits
  // so the same person filling the quiz again is recognisable as a returning
  // visitor. Stored in localStorage (first-party, same-browser). Re-read at
  // send time so multiple tabs opened simultaneously converge on one ID even
  // when they race on the first-ever write.
  const DEVICE_KEY = 'dvw_quiz_did';
  let _deviceId = '';
  try {
    _deviceId = localStorage.getItem(DEVICE_KEY) || '';
    if (!_deviceId) {
      _deviceId =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        String(Date.now()) + Math.random().toString(36).slice(2);
      try { localStorage.setItem(DEVICE_KEY, _deviceId); } catch (_) {}
    }
  } catch (_) {
    _deviceId = '';
  }
  // If another tab writes the device ID, adopt it so every tab in this browser
  // agrees on a single ID.
  try {
    window.addEventListener('storage', function (e) {
      if (e.key === DEVICE_KEY && e.newValue) _deviceId = e.newValue;
    });
  } catch (_) {}
  function deviceId() {
    try {
      var v = localStorage.getItem(DEVICE_KEY);
      if (v) { _deviceId = v; return v; }
    } catch (_) {}
    return _deviceId || SESSION_ID;
  }

  function track(event, data) {
    try {
      const payload = JSON.stringify(
        Object.assign({ event, session: SESSION_ID, device: deviceId(), ts: Date.now(), path: location.pathname }, data || {})
      );
      // Send as text/plain so the request stays CORS "simple" (no preflight).
      // Preflighted cross-site beacons are silently dropped by Safari/ITP, and
      // sendBeacon can also be blocked by ad blockers (returns false), so we
      // always fall back to a keepalive fetch to maximize delivery.
      const beaconed = navigator.sendBeacon
        ? navigator.sendBeacon(ANALYTICS_ENDPOINT, new Blob([payload], { type: 'text/plain' }))
        : false;
      if (!beaconed) {
        fetch(ANALYTICS_ENDPOINT, {
          method: 'POST', headers: { 'Content-Type': 'text/plain' },
          body: payload, keepalive: true, mode: 'cors',
        }).catch(() => {});
      }
    } catch (e) { /* analytics must never break the quiz */ }
  }

  // ── Static option metadata ──────────────────────────────────────────────
  const SHAPE_META = {
    round: {
      label: 'Round',
      svg: '<circle cx="50" cy="50" r="36" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="50" cy="50" r="2" fill="currentColor"/>',
    },
    square: {
      label: 'Not Round',
      svg: '<rect x="22" y="18" width="56" height="64" rx="6" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="50" cy="50" r="2" fill="currentColor"/>',
    },
    any: {
      label: 'No Preference',
      svg: '<circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="4 5"/>',
    },
  };
  const SHAPE_ORDER = ['round', 'square', 'any'];

  // ── R2 hero images ──────────────────────────────────────────────────────
  const R2 = 'https://pub-1b1e09168a714a9398e6d761466b572a.r2.dev/quiz-heroes';

  // Brand-specific static followup overrides (value, label, img, filterFn).
  // `ladiesOnly: true` triggers the ladies fallback logic.
  // `filterFn` receives a product record {id, f, s, b, l} and returns bool.
  const NAUTILUS_IMG = 'https://dannysvintagewatches.com/cdn/shop/files/DSCF3137copy2.jpg?v=1773601598&width=1000';

  // Brand-specific hero images (uploaded to R2 quiz-heroes/)
  const AP_DRESS_IMG       = `${R2}/audemars-piguet-dress.jpg`;
  const AP_INTEGRATED_IMG  = `${R2}/audemars-piguet-integrated.jpg`;
  const AP_LADIES_IMG      = `${R2}/audemars-piguet-ladies.jpg`;
  const CARTIER_SPORT_IMG  = `${R2}/cartier-sport.jpg`;
  const CARTIER_DRESS_IMG  = `${R2}/cartier-dress.jpg`;
  const CARTIER_LADIES_IMG = `${R2}/cartier-ladies.jpg`;
  const OMEGA_SPORT_IMG    = `${R2}/omega-sport.jpg`;
  const OMEGA_DRESS_IMG    = `${R2}/omega-dress.jpg`;
  const OMEGA_LADIES_IMG   = `${R2}/omega-ladies.webp`;
  const PIAGET_INTEGRATED_IMG = `${R2}/piaget-integrated.webp`;
  const PIAGET_DRESS_IMG   = `${R2}/piaget-dress.jpg`;
  const PIAGET_LADIES_IMG  = `${R2}/piaget-ladies.jpg`;
  const ROLEX_EVERYDAY_IMG = `${R2}/rolex-everyday.jpg`;
  const ROLEX_SPORT_IMG    = `${R2}/rolex-sport.jpg`;
  const ROLEX_DRESS_IMG    = `${R2}/rolex-dress.jpg`;
  const ROLEX_LADIES_IMG   = `${R2}/rolex-ladies.jpg`;
  const VC_DRESS_IMG       = `${R2}/vacheron-constantin-dress.webp`;
  const VC_INTEGRATED_IMG  = `${R2}/vacheron-constantin-integrated.jpg`;
  const VC_LADIES_IMG      = `${R2}/vacheron-constantin-ladies.jpg`;

  const BRAND_STATIC_FOLLOWUP = {
    'rolex': {
      title: 'What kind of Rolex are you after?',
      options: [
        { value: 'everyday', label: 'Everyday Classic', img: ROLEX_EVERYDAY_IMG, filterFn: r => r.f === 'everyday' },
        { value: 'sport',    label: 'Sport & Tool',     img: ROLEX_SPORT_IMG,    filterFn: r => r.f === 'sport' },
        { value: 'dress',    label: 'Dress & Formal',   img: ROLEX_DRESS_IMG,    filterFn: r => r.f === 'dress' },
        { value: 'ladies',   label: 'Ladies',           img: ROLEX_LADIES_IMG,   ladiesOnly: true },
      ],
    },
    'patek-philippe': {
      title: 'Which Patek Philippe style speaks to you?',
      options: [
        { value: 'sport',      label: 'Sport',      img: NAUTILUS_IMG,            filterFn: r => r.f === 'integrated' },
        // Integrated includes dress watches that wear on a bracelet (intersection)
        { value: 'integrated', label: 'Integrated', img: `${R2}/integrated.jpeg`, filterFn: r => r.f === 'integrated' || r.f === 'dress' },
        { value: 'dress',      label: 'Dress',      img: `${R2}/dress.jpeg`,      filterFn: r => r.f === 'dress' },
        { value: 'ladies',     label: 'Ladies',     img: `${R2}/ladies.jpeg`,     ladiesOnly: true },
      ],
    },
    'audemars-piguet': {
      title: 'Which AP style speaks to you?',
      options: [
        { value: 'sport',      label: 'Sport (Royal Oak)', img: `${R2}/sport.jpeg`, filterFn: r => r.f === 'sport' },
        // Integrated includes AP dress watches on integrated bracelets
        { value: 'integrated', label: 'Integrated',        img: AP_INTEGRATED_IMG,  filterFn: r => r.f === 'integrated' || r.f === 'dress' },
        { value: 'dress',      label: 'Dress',             img: AP_DRESS_IMG,       filterFn: r => r.f === 'dress' },
        { value: 'ladies',     label: 'Ladies',            img: AP_LADIES_IMG,      ladiesOnly: true },
      ],
    },
    'piaget': {
      title: 'Which Piaget style speaks to you?',
      options: [
        { value: 'integrated', label: 'Integrated', img: PIAGET_INTEGRATED_IMG, filterFn: r => r.f === 'integrated' || r.f === 'dress' },
        { value: 'dress',      label: 'Dress',      img: PIAGET_DRESS_IMG,     filterFn: r => r.f === 'dress' },
        { value: 'ladies',     label: 'Ladies',     img: PIAGET_LADIES_IMG,    ladiesOnly: true },
      ],
    },
    'vacheron-constantin': {
      title: 'Which Vacheron Constantin style speaks to you?',
      options: [
        { value: 'integrated', label: 'Integrated', img: VC_INTEGRATED_IMG, filterFn: r => r.f === 'integrated' || r.f === 'dress' },
        { value: 'dress',      label: 'Dress',      img: VC_DRESS_IMG,      filterFn: r => r.f === 'dress' },
        { value: 'ladies',     label: 'Ladies',     img: VC_LADIES_IMG,     ladiesOnly: true },
      ],
    },
    'cartier': {
      title: 'Which Cartier style speaks to you?',
      options: [
        { value: 'sport',  label: 'Sport (Santos)', img: CARTIER_SPORT_IMG,  filterFn: r => r.f === 'sport' },
        { value: 'dress',  label: 'Dress',          img: CARTIER_DRESS_IMG,  filterFn: r => r.f === 'dress' },
        { value: 'ladies', label: 'Ladies',         img: CARTIER_LADIES_IMG, ladiesOnly: true },
      ],
    },
    'omega': {
      title: 'Which Omega style speaks to you?',
      options: [
        { value: 'sport',  label: 'Sport',  img: OMEGA_SPORT_IMG,  filterFn: r => r.f === 'sport' },
        { value: 'dress',  label: 'Dress',  img: OMEGA_DRESS_IMG,  filterFn: r => r.f === 'dress' },
        { value: 'ladies', label: 'Ladies', img: OMEGA_LADIES_IMG, ladiesOnly: true },
      ],
    },
  };

  const BAND_ORDER = ['under-5k', '5-10k', '10-25k', '25k-plus'];
  const BAND_LABEL = {
    'under-5k': 'Under $5,000',
    '5-10k': '$5,000 – $10,000',
    '10-25k': '$10,000 – $25,000',
    '25k-plus': '$25,000+',
  };

  // ── DOM refs ──────────────────────────────────────────────────────────────
  const stepEls = Array.from(root.querySelectorAll('.dvw-quiz__step'));
  const stepByName = {};
  stepEls.forEach((s) => (stepByName[s.dataset.question] = s));
  const progressBar = root.querySelector('[data-progress]');
  const restartBtn = root.querySelector('[data-action="restart"]');
  const restartActions = root.querySelectorAll('[data-action="restart"]');

  const followupTitle = root.querySelector('[data-followup-title]');
  const followupOptions = root.querySelector('[data-followup-options]');
  const shapeOptions = root.querySelector('[data-shape-options]');
  const budgetOptions = root.querySelector('[data-budget-options]');

  const resultsGrid = root.querySelector('[data-results-grid]');
  const resultsCount = root.querySelector('[data-results-count]');
  const resultsSummary = root.querySelector('[data-results-summary]');
  const archiveWrap = root.querySelector('[data-archive]');
  const archiveGrid = root.querySelector('[data-archive-grid]');

  // ── Email capture ─────────────────────────────────────────────────────────
  // Two inline, no-popup touchpoints that both post the same 'email_capture'
  // analytics event, tied to whatever brand/style/shape/budget the shopper
  // picked, so every address collected is already "warm":
  //   1. Main capture — shown for every completed quiz. Copy adapts: "save
  //      this search" when live matches were found, "source this for me"
  //      when they weren't.
  //   2. Archive waitlist — a quieter continuation of the "Request a
  //      restock" CTAs, shown only when sold pieces match the same taste.
  const captureWrap = root.querySelector('[data-capture]');
  const captureLabel = root.querySelector('[data-capture-label]');
  const captureForm = root.querySelector('[data-capture-form]');
  const captureInput = root.querySelector('[data-capture-input]');
  const captureSubmit = root.querySelector('[data-capture-submit]');
  const captureNote = root.querySelector('[data-capture-note]');
  const captureSuccess = root.querySelector('[data-capture-success]');

  const waitlistForm = root.querySelector('[data-waitlist-form]');
  const waitlistInput = root.querySelector('[data-waitlist-input]');
  const waitlistSubmit = root.querySelector('[data-waitlist-submit]');
  const waitlistLabel = root.querySelector('[data-waitlist-label]');
  const waitlistSuccess = root.querySelector('[data-waitlist-success]');

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
  }

  function currentSelections() {
    return {
      brand: state.brandKey,
      brandLabel: state.brandLabel,
      style: state.selections.f || null,
      shape: state.selections.s || null,
      budget: state.selections.b || null,
      summary: state.summary.slice(),
    };
  }

  function submitLead(opts) {
    const value = (opts.email || '').trim();
    if (!isValidEmail(value)) {
      opts.formEl.classList.add('is-invalid');
      opts.inputEl.focus();
      return;
    }
    opts.formEl.classList.remove('is-invalid');
    opts.submitEl.disabled = true;
    opts.inputEl.disabled = true;
    track('email_capture', Object.assign(
      { context: opts.context, email: value, matchCount: opts.matchCount },
      currentSelections()
    ));
    opts.formEl.hidden = true;
    if (opts.noteEl) opts.noteEl.hidden = true;
    if (opts.successEl) opts.successEl.hidden = false;
  }

  function resetCapture() {
    if (!captureForm) return;
    captureForm.hidden = false;
    captureForm.classList.remove('is-invalid');
    captureInput.value = '';
    captureInput.disabled = false;
    captureSubmit.disabled = false;
    if (captureNote) captureNote.hidden = false;
    if (captureSuccess) captureSuccess.hidden = true;
  }

  function resetWaitlist() {
    if (!waitlistForm) return;
    waitlistForm.hidden = false;
    waitlistForm.classList.remove('is-invalid');
    waitlistInput.value = '';
    waitlistInput.disabled = false;
    waitlistSubmit.disabled = false;
    if (waitlistLabel) waitlistLabel.hidden = false;
    if (waitlistSuccess) waitlistSuccess.hidden = true;
  }

  if (captureForm) {
    captureForm.addEventListener('submit', (e) => {
      e.preventDefault();
      submitLead({
        context: captureForm.dataset.context || 'sourcing',
        email: captureInput.value,
        formEl: captureForm,
        inputEl: captureInput,
        submitEl: captureSubmit,
        noteEl: captureNote,
        successEl: captureSuccess,
        matchCount: view.matches.length,
      });
    });
    captureInput.addEventListener('input', () => captureForm.classList.remove('is-invalid'));
  }
  if (waitlistForm) {
    waitlistForm.addEventListener('submit', (e) => {
      e.preventDefault();
      submitLead({
        context: 'waitlist',
        email: waitlistInput.value,
        formEl: waitlistForm,
        inputEl: waitlistInput,
        submitEl: waitlistSubmit,
        noteEl: waitlistLabel,
        successEl: waitlistSuccess,
        matchCount: view.matches.length,
      });
    });
    waitlistInput.addEventListener('input', () => waitlistForm.classList.remove('is-invalid'));
  }

  const PROGRESS = { brand: 8, followup: 33, shape: 58, budget: 82, results: 100 };

  // ── State ─────────────────────────────────────────────────────────────────
  let manifest = null;
  const state = {
    brandKey: null,
    brandLabel: '',
    pool: [],
    facet: null,
    summary: [],
    selections: {},
    ladiesFallback: false,
    originalBrandForLadies: '',
  };

  function showStep(name) {
    stepEls.forEach((s) => s.classList.toggle('is-active', s.dataset.question === name));
    if (progressBar) progressBar.style.width = `${PROGRESS[name] || 0}%`;
    restartBtn.hidden = name === 'brand';
    if (backBtn) backBtn.hidden = name === 'brand';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Step history — Back goes one step, not all the way home ───────────────
  // Each shown step records the state as it was on entry (the pool is filtered
  // down as the shopper advances), so Back can restore the prior step's pool
  // and let them re-answer. From the first step (brand) Back falls back to home.
  const backBtn = root.querySelector('.dvw-quiz__back');
  let history = [];
  let currentEntry = null;

  function snapshot(name) {
    return {
      name,
      brandKey: state.brandKey,
      brandLabel: state.brandLabel,
      pool: state.pool.slice(),
      facet: state.facet,
      summary: state.summary.slice(),
      selections: Object.assign({}, state.selections),
      ladiesFallback: state.ladiesFallback,
      originalBrandForLadies: state.originalBrandForLadies,
    };
  }

  function restoreEntry(entry) {
    state.brandKey = entry.brandKey;
    state.brandLabel = entry.brandLabel;
    state.pool = entry.pool.slice();
    state.facet = entry.facet;
    state.summary = entry.summary.slice();
    state.selections = Object.assign({}, entry.selections);
    state.ladiesFallback = entry.ladiesFallback;
    state.originalBrandForLadies = entry.originalBrandForLadies;
  }

  function enterStep(name) {
    if (currentEntry) history.push(currentEntry);
    currentEntry = snapshot(name);
    showStep(name);
  }

  function resetHistory() {
    history = [];
    currentEntry = snapshot('brand');
  }

  function goBack() {
    if (!history.length) {
      window.location.href = '/';
      return;
    }
    if (currentEntry && currentEntry.name === 'results') teardownLoadMore();
    const prev = history.pop();
    restoreEntry(prev);
    currentEntry = prev;
    showStep(prev.name);
    const activeStep = stepByName[prev.name];
    if (activeStep) {
      activeStep
        .querySelectorAll('.is-selected')
        .forEach((el) => el.classList.remove('is-selected'));
    }
  }

  if (backBtn) {
    backBtn.addEventListener('click', (e) => {
      if (history.length) {
        e.preventDefault();
        goBack();
      }
    });
  }

  function selectVisual(container, btn) {
    container.querySelectorAll('.is-selected').forEach((el) => el.classList.remove('is-selected'));
    if (btn) btn.classList.add('is-selected');
  }

  // ── Brand step ──────────────────────────────────────────────────────────
  root.querySelectorAll('[data-brand-key]').forEach((btn) => {
    btn.addEventListener('click', () => onBrand(btn.dataset.brandKey, btn));
  });

  function onBrand(key, btn) {
    if (!manifest) return;
    const records = (manifest.products && manifest.products[key]) || [];
    const brandMeta = (manifest.brands || []).find((b) => b.key === key);
    state.brandKey = key;
    state.brandLabel = (brandMeta && brandMeta.label) || (key === 'any' ? 'Any brand' : key);
    state.pool = records.slice();
    state.facet = (manifest.facets && manifest.facets[key]) || { key: null, options: [] };
    state.summary = [];
    state.selections = {};
    if (key !== 'any') state.summary.push(state.brandLabel);
    selectVisual(btn.parentElement, btn);
    track('answer', { question: 'brand', value: key, label: state.brandLabel });

    // Hidden Gems: skip followup + shape, go straight to budget price differentiator.
    if (key === 'hidden-gems') {
      setTimeout(tryBudget, 200);
      return;
    }
    setTimeout(tryFollowup, 200);
  }

  // ── Step 2: dynamic follow-up ─────────────────────────────────────────────
  function makeTileBtn(value, label, imgUrl) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dvw-quiz__tile';
    btn.dataset.value = value;
    btn.innerHTML =
      `<div class="dvw-quiz__tile-media">` +
      `<img src="${imgUrl}" alt="${label}" loading="eager" decoding="async" class="dvw-quiz__tile-img">` +
      `<span class="dvw-quiz__tile-overlay"></span></div>` +
      `<span class="dvw-quiz__tile-label">${label}</span>`;
    return btn;
  }

  function tryFollowup() {
    const brandOverride = BRAND_STATIC_FOLLOWUP[state.brandKey];
    const f = state.facet;

    // Build the options list: static override takes priority, otherwise manifest options.
    let options = null;
    let useStatic = false;
    if (brandOverride) {
      options = brandOverride.options;
      useStatic = true;
      if (followupTitle) followupTitle.textContent = brandOverride.title;
    } else if (f && f.key && f.options && f.options.length >= 1) {
      if (followupTitle && f.title) followupTitle.textContent = f.title;
    } else {
      tryShape();
      return;
    }

    followupOptions.innerHTML = '';

    if (useStatic) {
      // Static brand-specific tiles with hero images.
      options.forEach((opt) => {
        if (opt.ladiesOnly) {
          const btn = makeTileBtn(opt.value, opt.label, opt.img);
          btn.addEventListener('click', () => {
            selectVisual(followupOptions, btn);
            const ladyPool = state.pool.filter(r => r.l === true);
            if (ladyPool.length === 0) {
              // No brand-specific ladies — fall back to all ladies, remember for message.
              const allLadies = [];
              if (manifest.products) {
                Object.values(manifest.products).forEach(arr => {
                  arr.forEach(r => { if (r.l) allLadies.push(r); });
                });
              }
              state.pool = allLadies;
              state.ladiesFallback = true;
              state.originalBrandForLadies = state.brandLabel;
              state.summary.push('Ladies');
            } else {
              state.pool = ladyPool;
              state.ladiesFallback = false;
              state.summary.push('Ladies');
            }
            state.selections.f = 'ladies';
            state.selections.archiveFilterFn = r => r.l === true;
            track('answer', { question: 'style', value: 'ladies', label: opt.label });
            setTimeout(tryShape, 200);
          });
          followupOptions.appendChild(btn);
        } else {
          const btn = makeTileBtn(opt.value, opt.label, opt.img);
          btn.addEventListener('click', () => {
            selectVisual(followupOptions, btn);
            state.pool = state.pool.filter(opt.filterFn);
            state.selections.f = opt.value;
            state.selections.archiveFilterFn = opt.filterFn; // so archive mirrors same filter
            state.summary.push(opt.label);
            track('answer', { question: 'style', value: opt.value, label: opt.label });
            setTimeout(tryShape, 200);
          });
          followupOptions.appendChild(btn);
        }
      });
    } else {
      // Dynamic manifest options rendered as image tiles using R2 hero images.
      const HERO_IMG = {
        sport: `${R2}/sport.jpeg`,
        everyday: `${R2}/everyday.jpeg`,
        integrated: `${R2}/integrated.jpeg`,
        dress: `${R2}/dress.jpeg`,
        ladies: `${R2}/ladies.jpeg`,
      };
      f.options.forEach((opt) => {
        const img = HERO_IMG[opt.value] || `${R2}/dress.jpeg`;
        const btn = makeTileBtn(opt.value, opt.label, img);
        btn.addEventListener('click', () => {
          selectVisual(followupOptions, btn);
          state.pool = state.pool.filter((r) => String(r.f) === String(opt.value));
          state.selections.f = opt.value;
          state.selections.archiveFilterFn = null; // dynamic path uses f equality
          state.summary.push(opt.label);
          track('answer', { question: 'style', value: opt.value, label: opt.label });
          setTimeout(tryShape, 200);
        });
        followupOptions.appendChild(btn);
      });
      // Ladies option — always appended to every brand's followup.
      const ladiesBtn = makeTileBtn('ladies', 'Ladies', `${R2}/ladies.jpeg`);
      ladiesBtn.addEventListener('click', () => {
        selectVisual(followupOptions, ladiesBtn);
        const ladyPool = state.pool.filter(r => r.l === true);
        if (ladyPool.length === 0) {
          const allLadies = [];
          if (manifest.products) {
            Object.values(manifest.products).forEach(arr => {
              arr.forEach(r => { if (r.l) allLadies.push(r); });
            });
          }
          state.pool = allLadies;
          state.ladiesFallback = true;
          state.originalBrandForLadies = state.brandLabel;
          state.summary.push('Ladies');
        } else {
          state.pool = ladyPool;
          state.ladiesFallback = false;
          state.summary.push('Ladies');
        }
        state.selections.f = 'ladies';
        state.selections.archiveFilterFn = r => r.l === true;
        track('answer', { question: 'style', value: 'ladies', label: 'Ladies' });
        setTimeout(tryShape, 200);
      });
      followupOptions.appendChild(ladiesBtn);
    }

    // "No preference" tile (ghost style, no image).
    const anyOcc = document.createElement('button');
    anyOcc.type = 'button';
    anyOcc.className = 'dvw-quiz__banner';
    anyOcc.dataset.value = 'any';
    anyOcc.innerHTML =
      `<span class="dvw-quiz__banner-icon" aria-hidden="true">` +
      `<svg viewBox="0 0 100 100" width="28" height="28"><circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="4 5"/></svg></span>` +
      `<span class="dvw-quiz__banner-text">No preference — show all styles</span>` +
      `<span class="dvw-quiz__banner-arrow" aria-hidden="true">→</span>`;
    anyOcc.addEventListener('click', () => {
      selectVisual(followupOptions, anyOcc);
      state.selections.f = null;
      state.selections.archiveFilterFn = null;
      state.summary.push('Any occasion');
      track('answer', { question: 'style', value: 'any', label: 'No preference' });
      setTimeout(tryShape, 200);
    });
    followupOptions.appendChild(anyOcc);
    enterStep('followup');
  }

  // ── Step 3: shape (always 3 options: Round / Not Round / No Preference) ──
  function tryShape() {
    if (!state.pool.length) { tryBudget(); return; }
    shapeOptions.innerHTML = '';
    SHAPE_ORDER.forEach((s) => {
      const meta = SHAPE_META[s];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dvw-quiz__tile';
      btn.dataset.value = s;
      btn.innerHTML =
        `<div class="dvw-quiz__tile-media dvw-quiz__tile-media--icon">` +
        `<svg viewBox="0 0 100 100" width="80" height="80" aria-hidden="true">${meta.svg}</svg></div>` +
        `<span class="dvw-quiz__tile-label">${meta.label}</span>`;
      btn.addEventListener('click', () => {
        selectVisual(shapeOptions, btn);
        if (s === 'round') {
          state.pool = state.pool.filter((r) => r.s === 'round');
          state.selections.s = 'round';
          state.summary.push('Round');
        } else if (s === 'square') {
          state.pool = state.pool.filter((r) => r.s !== 'round');
          state.selections.s = 'square';
          state.summary.push('Not Round');
        } else {
          state.selections.s = null;
          state.summary.push('Any shape');
        }
        track('answer', { question: 'shape', value: s, label: meta.label });
        setTimeout(tryBudget, 200);
      });
      shapeOptions.appendChild(btn);
    });
    enterStep('shape');
  }

  // ── Step 4: budget (only bands present in the current pool) ───────────────
  function tryBudget() {
    const present = BAND_ORDER.filter((b) => state.pool.some((r) => r.b === b));
    if (present.length < 2) {
      goResults();
      return;
    }
    budgetOptions.innerHTML = '';
    present.forEach((b) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dvw-quiz__pill';
      btn.dataset.value = b;
      btn.textContent = BAND_LABEL[b] || b;
      btn.addEventListener('click', () => {
        selectVisual(budgetOptions, btn);
        state.pool = state.pool.filter((r) => r.b === b);
        state.selections.b = b;
        state.summary.push(BAND_LABEL[b] || b);
        track('answer', { question: 'budget', value: b, label: BAND_LABEL[b] || b });
        setTimeout(goResults, 200);
      });
      budgetOptions.appendChild(btn);
    });
    // "No preference" — skip the budget filter, keep all bands in the pool.
    const anyBudget = document.createElement('button');
    anyBudget.type = 'button';
    anyBudget.className = 'dvw-quiz__pill dvw-quiz__pill--ghost';
    anyBudget.dataset.value = 'any';
    anyBudget.textContent = 'No preference';
    anyBudget.addEventListener('click', () => {
      selectVisual(budgetOptions, anyBudget);
      state.selections.b = null;
      state.summary.push('Any budget');
      track('answer', { question: 'budget', value: 'any', label: 'No preference' });
      setTimeout(goResults, 200);
    });
    budgetOptions.appendChild(anyBudget);
    enterStep('budget');
  }

  // ── Storefront live lookup (current price / image / availability) ─────────
  const NODES_QUERY = [
    'query Nodes($ids: [ID!]!) {',
    '  nodes(ids: $ids) {',
    '    ... on Product {',
    '      id title handle availableForSale',
    '      featuredImage { url altText }',
    '      priceRange { minVariantPrice { amount } }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  async function fetchNodes(ids) {
    const out = [];
    for (let i = 0; i < ids.length; i += 100) {
      const chunk = ids.slice(i, i + 100);
      const resp = await fetch(SF_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Shopify-Storefront-Access-Token': SF_TOKEN,
        },
        body: JSON.stringify({ query: NODES_QUERY, variables: { ids: chunk } }),
      });
      if (!resp.ok) throw new Error('Storefront HTTP ' + resp.status);
      const json = await resp.json();
      if (json.errors) throw new Error('Storefront errors: ' + JSON.stringify(json.errors));
      (json.data.nodes || []).forEach((n) => {
        if (n && n.id) out.push(n);
      });
    }
    return out;
  }

  function mapNode(n) {
    return {
      title: n.title,
      handle: n.handle,
      available: !!n.availableForSale,
      img: n.featuredImage ? n.featuredImage.url : '',
      price: n.priceRange && n.priceRange.minVariantPrice ? n.priceRange.minVariantPrice.amount : null,
    };
  }

  // ── Rendering ─────────────────────────────────────────────────────────────
  function formatPrice(amount) {
    const n = parseFloat(amount);
    if (isNaN(n)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(n);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function productCard(p, opts) {
    const sold = !!(opts && opts.sold);
    const price = formatPrice(p.price);
    const a = document.createElement('a');
    a.className = 'dvw-product' + (sold ? ' dvw-product--sold' : '');
    a.href = `/products/${p.handle}`;
    a.innerHTML =
      `<div class="dvw-product__media">` +
      (p.img ? `<img src="${p.img}" alt="${escapeHtml(p.title)}" loading="lazy" />` : '') +
      (sold ? '<span class="dvw-product__sold">Sold</span>' : '') +
      `</div>` +
      `<div class="dvw-product__info">` +
      `<h3 class="dvw-product__title">${escapeHtml(p.title)}</h3>` +
      (sold
        ? '<div class="dvw-product__price dvw-product__price--sold">Sold · request similar</div>'
        : price ? `<div class="dvw-product__price">${price}</div>` : '') +
      `</div>`;
    return a;
  }

  function renderSkeletons(n) {
    let html = '';
    for (let i = 0; i < n; i++) {
      html += `<div class="dvw-product"><div class="dvw-product__media dvw-skeleton"></div><div class="dvw-product__info"><div class="dvw-skeleton" style="height:14px;margin-bottom:8px;border-radius:2px;"></div><div class="dvw-skeleton" style="height:10px;width:40%;border-radius:2px;"></div></div></div>`;
    }
    return html;
  }

  // ── Fallback tiers ───────────────────────────────────────────────────────
  // Rule: a completed quiz path must never render an empty results section.
  // If the exact brand+style+shape+budget combo has zero matches, relax the
  // filters one at a time (budget, then shape, then style, then brand) and
  // finally fall back to the whole catalog. Every tier reuses the same
  // {id, s, b, f, l} records already in the manifest, so this works for both
  // in-stock results and the sold/archive lookup with the same logic.
  function flattenDataset(dataset) {
    const out = [];
    const seen = new Set();
    Object.values(dataset || {}).forEach((arr) => {
      (arr || []).forEach((r) => {
        if (!seen.has(r.id)) { seen.add(r.id); out.push(r); }
      });
    });
    return out;
  }

  function buildTierPools(dataset, brandKey, selections) {
    const sel = selections || {};
    const brandPool = (dataset && dataset[brandKey]) || [];
    const tiers = [];
    let lastLen = -1;
    const push = (pool, label) => {
      if (pool && pool.length && pool.length !== lastLen) {
        tiers.push({ pool, label });
        lastLen = pool.length;
      }
    };

    const byFacet = (arr) => {
      if (typeof sel.archiveFilterFn === 'function') return arr.filter(sel.archiveFilterFn);
      if (sel.f != null) return arr.filter((r) => String(r.f) === String(sel.f));
      return arr;
    };
    const byShape = (arr) => (sel.s != null ? arr.filter((r) => r.s === sel.s) : arr);
    const byBudget = (arr) => (sel.b != null ? arr.filter((r) => r.b === sel.b) : arr);

    push(byBudget(byShape(byFacet(brandPool))), 'exact');
    push(byShape(byFacet(brandPool)), 'drop-budget');
    push(byFacet(brandPool), 'drop-shape');
    push(brandPool, 'drop-style');

    const all = flattenDataset(dataset);
    push(byFacet(all), 'any-brand-same-style');
    push(all, 'any-brand');

    return tiers;
  }

  const FALLBACK_COPY = {
    'drop-budget': () => "Nothing at that exact price — here's the closest fit regardless of budget.",
    'drop-shape': () => "No exact case-shape match — here's the closest fit otherwise.",
    'drop-style': (brand) => `No exact style match${brand ? ` for ${brand}` : ''} — here's what else we have.`,
    'any-brand-same-style': () => "Not in that brand right now — here's the same style from our wider collection.",
    'any-brand': () => 'No exact matches — here is a look at what is currently available.',
  };

  async function resolveFallback(dataset, excludeIds, capPerTier) {
    if (!SF_TOKEN || !dataset) return null;
    const tiers = buildTierPools(dataset, state.brandKey, state.selections).filter(
      (t) => t.label !== 'exact'
    );
    for (const tier of tiers) {
      const ids = tier.pool.map((r) => r.id).slice(0, capPerTier || 200);
      if (!ids.length) continue;
      let nodes;
      try {
        nodes = await fetchNodes(ids);
      } catch (e) { continue; }
      const byId = {};
      nodes.forEach((nd) => (byId[nd.id] = nd));
      const records = ids
        .map((id) => byId[id])
        .filter((nd) => nd && nd.availableForSale)
        .map(mapNode);
      if (records.length) return { records, label: tier.label };
    }
    return null;
  }

  // ── Results ───────────────────────────────────────────────────────────────
  const view = { matches: [], shown: 0 };
  let loadObserver = null;
  let sentinel = null;

  function teardownLoadMore() {
    if (loadObserver) { loadObserver.disconnect(); loadObserver = null; }
    if (sentinel && sentinel.parentElement) sentinel.parentElement.removeChild(sentinel);
    sentinel = null;
  }

  function appendNextPage() {
    const slice = view.matches.slice(view.shown, view.shown + RESULTS_PER_PAGE);
    const frag = document.createDocumentFragment();
    slice.forEach((p) => frag.appendChild(productCard(p)));
    resultsGrid.appendChild(frag);
    view.shown += slice.length;
  }

  function ensureLoadMore() {
    teardownLoadMore();
    if (view.shown >= view.matches.length) return;
    sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    resultsGrid.parentElement.insertBefore(sentinel, resultsGrid.nextSibling);
    loadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && view.shown < view.matches.length) appendNextPage();
        if (view.shown >= view.matches.length) teardownLoadMore();
      },
      { rootMargin: '400px' }
    );
    loadObserver.observe(sentinel);
  }

  async function goResults() {
    teardownLoadMore();
    enterStep('results');
    resultsCount.textContent = 'Finding your matches…';
    resultsSummary.textContent = state.summary.join(' · ');
    resultsGrid.innerHTML = renderSkeletons(8);
    if (archiveWrap) archiveWrap.hidden = true;

    if (!SF_TOKEN) {
      resultsCount.textContent = 'Search is temporarily unavailable.';
      resultsGrid.innerHTML = '';
      return;
    }

    const ids = state.pool.map((r) => r.id);
    let nodes;
    try {
      nodes = await fetchNodes(ids);
    } catch (e) {
      resultsCount.textContent = 'Something went wrong loading watches.';
      resultsGrid.innerHTML = '';
      return;
    }

    // Keep the manifest ordering (newest first) and drop anything now sold.
    const byId = {};
    nodes.forEach((n) => (byId[n.id] = n));
    view.matches = ids
      .map((id) => byId[id])
      .filter((n) => n && n.availableForSale)
      .map(mapNode);
    view.shown = 0;
    resultsGrid.innerHTML = '';

    const n = view.matches.length;
    track('results', {
      brand: state.brandKey,
      summary: state.summary,
      matchCount: n,
      answers: {
        brand: state.brandKey,
        style: state.selections.f || null,
        shape: state.selections.s || null,
        budget: state.selections.b || null,
      },
      rebuttal: n === 0 ? (state.ladiesFallback ? 'ladies-fallback' : 'no-inventory') : undefined,
    });

    if (n === 0) {
      const fallback = await resolveFallback(manifest && manifest.products, ids);
      if (fallback) {
        view.matches = fallback.records;
        view.shown = 0;
        const count = fallback.records.length;
        resultsCount.textContent = `${count} watch${count === 1 ? '' : 'es'} you might like`;
        resultsSummary.textContent = (FALLBACK_COPY[fallback.label] || (() => ''))(state.brandLabel);
        appendNextPage();
        ensureLoadMore();
        track('results_fallback', { brand: state.brandKey, tier: fallback.label, matchCount: count });
      } else {
        resultsCount.textContent = 'No matches in stock right now.';
        resultsSummary.textContent =
          'These just sold or moved — browse the full collection, or see recently sold pieces below.';
      }
    } else if (state.ladiesFallback) {
      resultsCount.textContent = `${n} ladies watch${n === 1 ? '' : 'es'} found`;
      resultsSummary.textContent =
        `No ${state.originalBrandForLadies} ladies in stock right now — but check out these other ladies pieces`;
      appendNextPage();
      ensureLoadMore();
    } else {
      resultsCount.textContent = `${n} watch${n === 1 ? '' : 'es'} match`;
      appendNextPage();
      ensureLoadMore();
    }

    if (captureWrap) {
      resetCapture();
      captureWrap.hidden = false;
      const hasMatches = view.matches.length > 0;
      captureForm.dataset.context = hasMatches ? 'save_search' : 'sourcing';
      captureLabel.textContent = hasMatches
        ? 'Save these preferences for faster browsing next time, or to hear about similar pieces first.'
        : "Looking for a specific reference? Leave your email and our team will notify you first when a matching piece enters our inventory.";
      captureSubmit.innerHTML = (hasMatches ? 'Save' : 'Notify me') +
        '<span class="dvw-quiz__capture-arrow" aria-hidden="true">\u2192</span>';
    }

    fetchArchive();
  }

  // ── Archive (sold pieces matching the same path) ──────────────────────────
  // Same relaxation rule as live results: try the exact facet/shape/budget
  // combo first, then progressively widen (drop budget, shape, style, brand)
  // until a tier actually has sold pieces to show, or we run out of tiers.
  async function fetchArchive() {
    if (!archiveWrap || !archiveGrid || !SF_TOKEN || !manifest || !manifest.archive) {
      if (archiveWrap) archiveWrap.hidden = true;
      return;
    }
    const tiers = buildTierPools(manifest.archive, state.brandKey, state.selections);
    for (const tier of tiers) {
      const ids = tier.pool.map((r) => r.id).slice(0, ARCHIVE_MAX + 20);
      if (!ids.length) continue;
      let nodes;
      try {
        nodes = await fetchNodes(ids);
      } catch (e) { continue; }
      const sold = nodes.filter((n) => !n.availableForSale).slice(0, ARCHIVE_MAX).map(mapNode);
      if (sold.length) {
        const frag = document.createDocumentFragment();
        sold.forEach((p) => frag.appendChild(productCard(p, { sold: true })));
        archiveGrid.innerHTML = '';
        archiveGrid.appendChild(frag);
        archiveWrap.hidden = false;
        resetWaitlist();
        track('archive_shown', { brand: state.brandKey, count: sold.length, tier: tier.label });
        return;
      }
    }
    archiveWrap.hidden = true;
  }

  // ── Restart ───────────────────────────────────────────────────────────────
  restartActions.forEach((btn) =>
    btn.addEventListener('click', () => {
      state.brandKey = null;
      state.pool = [];
      state.facet = null;
      state.summary = [];
      state.selections = {};
      state.ladiesFallback = false;
      state.originalBrandForLadies = '';
      stepEls.forEach((s) => s.querySelectorAll('.is-selected').forEach((el) => el.classList.remove('is-selected')));
      teardownLoadMore();
      if (captureWrap) captureWrap.hidden = true;
      resetCapture();
      resetWaitlist();
      showStep('brand');
      resetHistory();
    })
  );

  // ── Init ──────────────────────────────────────────────────────────────────
  async function init() {
    showStep('brand');
    resetHistory();
    if (!MANIFEST_URL) {
      resultsCount.textContent = 'Quiz data unavailable.';
      return;
    }
    try {
      const resp = await fetch(MANIFEST_URL, { credentials: 'same-origin' });
      manifest = await resp.json();
    } catch (e) {
      manifest = null;
    }
    // Pre-warm the browser cache for all hero images so the followup step
    // renders without any loading delay even on slow connections.
    const toPreload = new Set([
      `${R2}/sport.jpeg`, `${R2}/integrated.jpeg`,
      `${R2}/dress.jpeg`, `${R2}/ladies.jpeg`,
      NAUTILUS_IMG,
    ]);
    Object.values(BRAND_STATIC_FOLLOWUP).forEach(b =>
      b.options.forEach(o => { if (o.img) toPreload.add(o.img); })
    );
    toPreload.forEach(url => { const img = new Image(); img.src = url; });
  }

  init();
})();
